import _ from 'lodash';
import { StoreState } from '../../../reducers/store.types';
import { Dispatch, GetState, ThunkAction } from '../../../redux.types';
import { getApplications } from '../../../../selectors/applications/applications';
import { getSelectedOrganizationId } from '../../../../selectors/organizations/organizations';
import { RouterContext } from '../../../../types/router';
import {
  getDefaultProjectId,
  getRawProjects,
} from '../../../projects/projects.selectors';
import { Action } from 'redux';

const hasProjectIdChanged = (
  prevState?,
  nextState?,
  prevRouter?,
  nextRouter?,
) => {
  return (
    getDefaultProjectId(prevState, { router: prevRouter }) !==
    getDefaultProjectId(nextState, { router: nextRouter })
  );
};

const hasOrganizationIdChanged = (
  prevState?,
  nextState?,
  prevRouter?,
  nextRouter?,
) => {
  return (
    getSelectedOrganizationId(prevState, { router: prevRouter }) !==
    getSelectedOrganizationId(nextState, { router: nextRouter })
  );
};

export const doNotFetchData = () => {
  return false;
};

export const fetchDataOnOrganizationChange = (...args: any[]) => {
  return hasOrganizationIdChanged(...args);
};

export const fetchDataOnProjectChange = (...args: any[]) => {
  return hasProjectIdChanged(...args) || hasOrganizationIdChanged(...args);
};

export const fetchDataOnAllApplicationsChange = (
  prevState: StoreState,
  nextState: StoreState,
  prevRouter: any,
  nextRouter: any,
) => {
  return (
    hasOrganizationIdChanged(prevState, nextState, prevRouter, nextRouter) ||
    getApplications(prevState) !== getApplications(nextState) ||
    getRawProjects(prevState) !== getRawProjects(nextState)
  );
};

export type ShouldFetchDataFn = (
  prevState: StoreState,
  state: StoreState,
  prevContext: RouterContext,
  context: RouterContext,
) => boolean;
export type LoadDataFn = (
  context: RouterContext,
  dispatch: Dispatch,
  getState: GetState,
) => Promise<any>;

export const buildLoadDataCachedFn = (
  loadDataFn: LoadDataFn,
  shouldFetchData: ShouldFetchDataFn,
): LoadDataFn => {
  let prevContext;
  let prevState;
  let result;

  return (context: RouterContext, dispatch: Dispatch, getState: GetState) => {
    // if result exists but promise is in status pending
    if (result && !prevState) {
      prevState = getState();
    }

    // if results exists and shouldFetchData returns true
    // return cached result
    if (
      result &&
      !shouldFetchData(prevState, getState(), prevContext, context)
    ) {
      prevContext = context;
      prevState = getState();
      return result;
    }

    prevContext = context;
    result = loadDataFn(context, dispatch, getState);
    return result.then(
      (response) => {
        // save prevState after store updated
        prevState = getState();
        return response;
      },

      (reason) => {
        prevState = getState();
        return Promise.reject(reason);
      },
    );
  };
};

export type ShouldDispatchFn = (
  prevState: StoreState,
  state: StoreState,
  prevContext: RouterContext,
  context: RouterContext,
) => boolean;
export type ActionCreatorFn = (
  context: RouterContext,
  state: StoreState,
) => Action;

export const buildCachedDispatch = (
  actionCreator: ActionCreatorFn,
  shouldDispatch: ShouldDispatchFn,
) => {
  let prevState: StoreState;
  let prevContext: RouterContext;

  return (dispatch: Dispatch, getState: () => StoreState) =>
    (context: RouterContext) => {
      const currentState = getState();

      if (
        !prevState ||
        shouldDispatch(prevState, currentState, prevContext, context)
      ) {
        const action = actionCreator(context, currentState);
        prevState = currentState;
        prevContext = context;
        dispatch(action);
      }
    };
};

export function workWithRouterParams<TContext, TResult>(
  getCallParams: (TContext, Dispatch, GetState) => any | Promise<any[]>,
  mainFunction: (...args: any[]) => (Dispatch, GetState) => TResult,
) {
  return (context: TContext) =>
    (dispatch: Dispatch, getState: GetState): Promise<TResult> => {
      return Promise.resolve(getCallParams(context, dispatch, getState)).then(
        (callParamsList: any[]) =>
          mainFunction(...callParamsList)(dispatch, getState),
      );
    };
}

function find(map, keyToFind) {
  for (const [key, value] of map) {
    if (_.isEqual(keyToFind, key)) {
      return value;
    }
  }
}

interface BuildMemoizedLoadFn {
  // arity 0
  (f: () => ThunkAction<Promise<any>>): () => ThunkAction<Promise<void>>;

  // arity 1
  <P1>(f: (p: P1) => ThunkAction<Promise<any>>): (
    p: P1,
  ) => ThunkAction<Promise<void>>;

  // arity 2
  <P1, P2>(f: (p: P1, p2: P2) => ThunkAction<Promise<any>>): (
    p: P1,
    p2: P2,
  ) => ThunkAction<Promise<void>>;
}

export const buildMemoizedLoadFn: BuildMemoizedLoadFn = (
  loadDataFn: (...args: any[]) => any,
): ((...args: any[]) => ThunkAction<Promise<void>>) => {
  const memoizeCache = new Map();

  return (...args: any[]) =>
    (dispatch: Dispatch, getState: GetState): Promise<void> => {
      const memoizedValue = find(memoizeCache, args);

      if (!_.isUndefined(memoizedValue)) {
        // Resolved with no arguments. This is done intentionally. Do not rely on promise results in
        // action creators. Rely on selectors instead.
        return Promise.resolve();
      }

      const loadResultOrPromise = loadDataFn(...args)(dispatch, getState);

      loadResultOrPromise.then(() => {
        // Add to cache after the load operation is completed
        memoizeCache.set(args, { _cached_: true });
      });

      return loadResultOrPromise;
    };
};
